import type { Schemas } from "@artisan/api-client";
import { Badge, Button, Card, cn } from "@artisan/ui";
import { ArrowRight, Briefcase, CloudOff, MapPin, Plus, Ruler } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { PlanView } from "../components/plan-view";
import { api, unwrap } from "../lib/api";
import { activeOrg } from "../lib/auth";
import { useOwner, useSession } from "../lib/hooks";
import { relativeMn } from "../lib/time";
import { useOutboxStatus } from "../outbox/outbox";
import { InstallHint } from "../components/install-hint";
import { createProject, type DealSeed, projectTitle } from "../project/actions";
import type { Project } from "../project/model";
import { statusBadge, steps } from "../project/progress";
import { listProjects, useProjectsVersion } from "../project/store";

type Deal = Schemas["DealOut"];

function useOpenDeals(): { deals: Deal[] | null; failed: boolean } {
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    unwrap(api().GET("/v1/crm/deals", { params: { query: { status: "open", limit: 6, sort: "-updated_at" } } }))
      .then((page) => live && setDeals(page.items))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);
  return { deals, failed };
}

function ProjectCard({ project }: { project: Project }) {
  const badge = statusBadge(project);
  const done = steps(project).filter((s) => s.done).length;
  const room = project.rooms[0];
  return (
    <Link to={`/p/${project.id}`} className="group block rounded-xl outline-none focus-visible:shadow-ring">
      <Card className="h-full overflow-hidden p-0 transition-shadow group-hover:shadow-md">
        <div className="relative grid h-40 place-items-center bg-drafting-grid">
          {project.render.approved?.output_url ? (
            <img src={project.render.approved.output_url} alt="" className="absolute inset-0 size-full object-cover" />
          ) : room ? (
            <PlanView room={room} compact className="h-32 w-auto max-w-[80%]" />
          ) : (
            <Ruler className="size-8 text-muted-foreground/50" aria-hidden />
          )}
          <Badge variant={badge.variant} className="absolute top-3 left-3">
            {badge.label}
          </Badge>
        </div>
        <div className="space-y-1.5 p-4">
          <h3 className="truncate text-[16px] font-semibold">{projectTitle(project)}</h3>
          <p className="flex items-center gap-1.5 truncate text-[13px] text-muted-foreground">
            <MapPin className="size-3.5 shrink-0" aria-hidden />
            {project.site.address || "Хаяг оруулаагүй"}
          </p>
          <div className="flex items-center justify-between pt-1 text-xs text-muted-foreground">
            <span>
              {done}/6 алхам · {project.rooms.length} өрөө
            </span>
            <span>{relativeMn(project.edited_at)}</span>
          </div>
          <div className="flex gap-1 pt-1" aria-hidden>
            {steps(project).map((s) => (
              <span key={s.key} className={cn("h-1 flex-1 rounded-full", s.done ? "bg-primary" : "bg-muted")} />
            ))}
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function HomeScreen() {
  const session = useSession();
  const owner = useOwner();
  useProjectsVersion();
  const projects = listProjects(owner);
  const outbox = useOutboxStatus();
  const navigate = useNavigate();
  const { deals, failed } = useOpenDeals();
  const org = activeOrg(session);
  const firstName = session.user.display_name.split(/\s+/)[0] || session.user.display_name;

  async function start(deal?: DealSeed) {
    const p = await createProject(session, deal);
    navigate(`/p/${p.id}/site`);
  }

  const active = projects.filter((p) => statusBadge(p).variant !== "success");
  return (
    <div className="mx-auto max-w-[1180px] px-8 pt-8 pb-12">
      <header className="mb-7 flex items-end justify-between gap-6">
        <div>
          <h1 className="font-display text-[34px] leading-tight font-semibold tracking-tight">Сайн байна уу, {firstName}</h1>
          <p className="mt-1 text-[15px] text-muted-foreground">
            {org?.name ?? "Байгууллага сонгоогүй"} · {active.length} идэвхтэй төсөл
            {outbox.pending ? ` · ${outbox.pending} илгээх дараалалд` : ""}
          </p>
        </div>
        {!outbox.online ? (
          <Badge variant="warning" icon={CloudOff}>
            Офлайн: бүгд төхөөрөмжид хадгалагдана
          </Badge>
        ) : null}
      </header>

      <InstallHint />

      <section className="grid grid-cols-[1.25fr_1fr] gap-5">
        <Card className="relative overflow-hidden border-primary/15 bg-primary-soft p-7">
          <Badge variant="default" icon={Ruler} className="self-start">
            Хэмжилт
          </Badge>
          <h2 className="mt-4 font-display text-[26px] font-semibold tracking-tight">Шинэ айлд хэмжилт хийх</h2>
          <p className="mt-2 max-w-[22rem] text-[15px] text-muted-foreground">
            Ханын урт, диагональ, нээлхий, инженерийн цэгийг хэмжээд бүсээ хуваана. Өнцгийг таамаглахгүй, өрөө битүүрэхийг апп шалгана.
          </p>
          <Button size="lg" className="mt-6 self-start" icon={Plus} onClick={() => void start()}>
            Шинэ төсөл эхлэх
          </Button>
          <svg viewBox="0 0 220 160" className="pointer-events-none absolute top-8 -right-2 w-[210px] opacity-90" aria-hidden>
            <g fill="none" stroke="var(--primary)" strokeWidth="1.4">
              <path d="M20 110 L110 60 L200 110 L110 150 Z" fill="var(--card)" />
              <path d="M110 60 L110 20 L200 70 L200 110" fill="color-mix(in oklab, var(--primary) 6%, var(--card))" />
              <path d="M20 110 L20 70 L110 20" fill="color-mix(in oklab, var(--primary) 10%, var(--card))" />
              <path d="M40 99 L40 80 L80 58 L80 77 Z M120 77 L120 58 L170 86 L170 105" fill="color-mix(in oklab, var(--oak) 45%, var(--card))" stroke="var(--oak)" />
              <path d="M60 128 L150 78" strokeDasharray="4 3" />
            </g>
          </svg>
        </Card>
        <Card className="p-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-[16px] font-semibold">
              <Briefcase className="size-4 text-muted-foreground" aria-hidden /> CRM-ийн нээлттэй захиалгууд
            </h2>
          </div>
          {deals === null && !failed ? (
            <p className="text-sm text-muted-foreground">Ачаалж байна…</p>
          ) : failed ? (
            <p className="text-sm text-muted-foreground">Интернэт орж ирэхэд CRM-ийн захиалгууд энд харагдана.</p>
          ) : deals && deals.length === 0 ? (
            <p className="text-sm text-muted-foreground">Нээлттэй захиалга алга. Шинэ төсөл эхлүүлнэ үү.</p>
          ) : (
            <ul className="divide-y">
              {(deals ?? []).slice(0, 4).map((d) => {
                const started = projects.find((p) => p.deal_id === d.id);
                return (
                  <li key={d.id} className="flex items-center gap-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{d.title}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        #{d.number} · {d.contact?.name ?? "Харилцагчгүй"}
                        {d.site_address ? ` · ${d.site_address}` : ""}
                      </p>
                    </div>
                    {started ? (
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/p/${started.id}`}>Нээх</Link>
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" iconRight={ArrowRight} onClick={() => void start(d)}>
                        Хэмжих
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </section>

      <section className="mt-9">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="font-display text-[20px] font-semibold tracking-tight">Миний төслүүд</h2>
          <span className="text-sm text-muted-foreground">{projects.length} төсөл энэ төхөөрөмжид</span>
        </div>
        {projects.length === 0 ? (
          <Card className="grid place-items-center gap-2 border-dashed py-14 text-center">
            <Ruler className="size-7 text-muted-foreground" aria-hidden />
            <p className="font-medium">Төсөл алга</p>
            <p className="max-w-sm text-sm text-muted-foreground">Шинэ төсөл эхлүүлэх эсвэл CRM-ийн захиалгаас хэмжилт эхлүүлнэ үү.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-3 gap-5">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
